/**
 * Secure token storage utility
 * Handles access and refresh token storage with proper security practices
 */

const ACCESS_TOKEN_KEY = 'hopsa_access_token';
const REFRESH_TOKEN_KEY = 'hopsa_refresh_token';
const USER_KEY = 'hopsa_user';
const ANONYMOUS_TOKEN_KEY = 'hopsa_anonymous_token';
const ANONYMOUS_PLAN_ID_KEY = 'hopsa_anonymous_plan_id';

/**
 * Token storage interface
 */
export interface TokenData {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt?: number;
  refreshTokenExpiresAt?: number;
}

/**
 * User data storage interface
 */
export interface StoredUser {
  id: string;
  email: string;
  name: string;
  displayName?: string;
  tier: string;
  emailVerified: boolean;
}

class TokenStorageService {
  /**
   * Store authentication tokens securely
   */
  setTokens(tokenData: TokenData): void {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokenData.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refreshToken);

      // Store access token expiration
      if (tokenData.accessTokenExpiresAt) {
        localStorage.setItem(
          `${ACCESS_TOKEN_KEY}_expires`,
          tokenData.accessTokenExpiresAt.toString()
        );
      }

      // Store refresh token expiration (defaults to 1 week if not provided)
      const refreshTokenExpiresAt =
        tokenData.refreshTokenExpiresAt || Date.now() + 7 * 24 * 60 * 60 * 1000; // 1 week
      localStorage.setItem(
        `${REFRESH_TOKEN_KEY}_expires`,
        refreshTokenExpiresAt.toString()
      );
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to retrieve access token:', error);
      return null;
    }
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error);
      return null;
    }
  }

  /**
   * Get all stored tokens
   */
  getTokens(): TokenData | null {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !refreshToken) {
      return null;
    }

    const accessTokenExpiresAtStr = localStorage.getItem(
      `${ACCESS_TOKEN_KEY}_expires`
    );
    const refreshTokenExpiresAtStr = localStorage.getItem(
      `${REFRESH_TOKEN_KEY}_expires`
    );

    const accessTokenExpiresAt = accessTokenExpiresAtStr
      ? parseInt(accessTokenExpiresAtStr, 10)
      : undefined;
    const refreshTokenExpiresAt = refreshTokenExpiresAtStr
      ? parseInt(refreshTokenExpiresAtStr, 10)
      : undefined;

    return {
      accessToken,
      refreshToken,
      accessTokenExpiresAt,
      refreshTokenExpiresAt,
    };
  }

  /**
   * Check if access token is expired
   */
  isAccessTokenExpired(): boolean {
    const expiresAtStr = localStorage.getItem(`${ACCESS_TOKEN_KEY}_expires`);
    if (!expiresAtStr) {
      return false; // If no expiration time, assume not expired
    }

    const expiresAt = parseInt(expiresAtStr, 10);
    const now = Date.now();

    // Consider token expired if it expires within the next 5 minutes
    const BUFFER_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
    return now >= expiresAt - BUFFER_TIME;
  }

  /**
   * Check if refresh token is expired
   */
  isRefreshTokenExpired(): boolean {
    const expiresAtStr = localStorage.getItem(`${REFRESH_TOKEN_KEY}_expires`);
    if (!expiresAtStr) {
      return false; // If no expiration time, assume not expired
    }

    const expiresAt = parseInt(expiresAtStr, 10);
    const now = Date.now();

    return now >= expiresAt;
  }

  /**
   * Check if we have valid tokens (refresh token not expired)
   */
  hasValidTokens(): boolean {
    const tokens = this.getTokens();
    return tokens !== null && !this.isRefreshTokenExpired();
  }

  /**
   * Clear all stored tokens
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(`${ACCESS_TOKEN_KEY}_expires`);
      localStorage.removeItem(`${REFRESH_TOKEN_KEY}_expires`);
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  /**
   * Store user data
   */
  setUser(user: StoredUser): void {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Failed to store user data:', error);
    }
  }

  /**
   * Get stored user data
   */
  getUser(): StoredUser | null {
    try {
      const userStr = localStorage.getItem(USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Failed to retrieve user data:', error);
      return null;
    }
  }

  /**
   * Clear stored user data
   */
  clearUser(): void {
    try {
      localStorage.removeItem(USER_KEY);
    } catch (error) {
      console.error('Failed to clear user data:', error);
    }
  }

  /**
   * Clear all stored authentication data
   */
  clearAll(): void {
    this.clearTokens();
    this.clearUser();
  }

  getAnonymousToken(): string | null {
    try {
      return localStorage.getItem(ANONYMOUS_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  setAnonymousToken(token: string, planId: string): void {
    try {
      localStorage.setItem(ANONYMOUS_TOKEN_KEY, token);
      localStorage.setItem(ANONYMOUS_PLAN_ID_KEY, planId);
    } catch (error) {
      console.error('Failed to store anonymous token:', error);
    }
  }

  clearAnonymousToken(): void {
    try {
      localStorage.removeItem(ANONYMOUS_TOKEN_KEY);
      localStorage.removeItem(ANONYMOUS_PLAN_ID_KEY);
    } catch {
      // ignore
    }
  }

  getAnonymousPlanId(): string | null {
    try {
      return localStorage.getItem(ANONYMOUS_PLAN_ID_KEY);
    } catch {
      return null;
    }
  }
}

export const tokenStorage = new TokenStorageService();
